import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { TechDossierModal, downloadTechDossierPdf } from './components/TechDossierModal';
import { DepthStage } from './components/DepthStage';
import {
  CodeIcon,
  SearchIcon,
  CloudIcon,
  MobileIcon,
  CheckIcon,
  PhoneIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  DocIcon,
  MailIcon,
  PinIcon,
  DownloadIcon,
} from './components/Icons';
import './LandingPage.css';

const STUDIO_WHATSAPP = '917808089090';

// 6 High-credibility FAQ items addressing SEO, architecture, timelines, and costs
const FAQ_ITEMS = [
  {
    id: 'faq-1',
    question: 'What core software engineering and web development services does Bramhaware provide?',
    answer:
      'Bramhaware Technologies is an engineering studio in Pune, India. We engineer custom web applications (React 19, Next.js, Node.js, Spring Boot), high-impact responsive corporate websites, cross-platform mobile apps (Flutter, React Native), and enterprise technical SEO architectures engineered for top Google rankings.',
  },
  {
    id: 'faq-2',
    question: 'How does your Technical SEO service ensure Page 1 Google visibility?',
    answer:
      'We treat SEO as core software architecture, not marketing fluff. Every web application we deliver features sub-second Core Web Vitals (LCP, CLS, INP), dynamic server-side pre-rendering, Schema.org semantic knowledge graphs, and Generative Engine Optimization (AEO) designed to capture citations in Google SGE, Perplexity, and ChatGPT.',
  },
  {
    id: 'faq-3',
    question: 'How do you balance the mixed-media tactile aesthetic with high conversion rates?',
    answer:
      'In IT, clarity is king. Artistic mixed-media elements—like washi tape, torn paper borders, and rubber stamps—are strictly used as accent pieces to give your brand warmth and personality. Your value proposition, services, and conversion call-to-actions (CTAs) remain prominent, clean, and frictionless.',
  },
  {
    id: 'faq-4',
    question: 'What is your typical project delivery timeline and agile sprint structure?',
    answer:
      'We run bi-weekly agile development sprints with continuous automated CI/CD staging environments. MVP platforms and bespoke SaaS solutions typically reach production in 4 to 8 weeks, complete with full source code ownership, unit test coverage, and a 90-day post-launch warranty.',
  },
  {
    id: 'faq-5',
    question: 'Can you build both mobile apps and web platforms under one unified backend?',
    answer:
      'Yes. Our multi-platform engineering squad designs unified API backends and design systems that power responsive web portals and native iOS & Android applications simultaneously, reducing development costs and ensuring consistent business logic across all touchpoints.',
  },
  {
    id: 'faq-6',
    question: 'How do we get started or request an architectural consultation?',
    answer:
      'You can submit your requirements via our interactive RFP form below, email us directly at info@bramhaware.in, or call our Pune studio helpline at +91 78080 89090. We typically respond within 4 business hours with an initial architectural roadmap.',
  },
];

export default function LandingPage() {
  const [emailInput, setEmailInput] = useState('');
  const [activeFaq, setActiveFaq] = useState('faq-1');
  const [isDossierOpen, setIsDossierOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'paper');
    document.body.style.backgroundColor = '#faf8f5';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  // RFP / Quote Form State
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'Custom Web Application Development',
    budget: '₹4L – ₹12L',
    timeline: '1 to 2 Months',
    message: '',
  });

  const handleHeroEmailSubmit = (e) => {
    e.preventDefault();
    if (!emailInput.trim()) return;
    setQuoteForm((prev) => ({ ...prev, email: emailInput }));
    const rfpElement = document.getElementById('rfp-section');
    if (rfpElement) {
      rfpElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleQuoteFormChange = (e) => {
    const { name, value } = e.target;
    setQuoteForm((prev) => ({ ...prev, [name]: value }));
  };

  // The brief is delivered to the studio's WhatsApp, pre-filled for the visitor to send
  const buildWhatsAppBriefUrl = (form) => {
    const lines = [
      'Hello Bramhaware, I would like a project proposal.',
      '',
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.phone ? `Phone: ${form.phone}` : null,
      `Service: ${form.service}`,
      `Budget: ${form.budget}`,
      `Launch: ${form.timeline}`,
      form.message ? `Scope: ${form.message}` : null,
    ].filter((line) => line !== null);
    return `https://wa.me/${STUDIO_WHATSAPP}?text=${encodeURIComponent(lines.join('\n'))}`;
  };

  const handleQuoteFormSubmit = (e) => {
    e.preventDefault();
    if (!quoteForm.name || !quoteForm.email) return;
    window.open(buildWhatsAppBriefUrl(quoteForm), '_blank', 'noopener,noreferrer');
    setIsSubmitted(true);
  };

  // Organization + WebSite drive the logo and site name Google shows in results;
  // ProfessionalService carries the local-business details.
  const organization = {
    '@type': ['Organization', 'ProfessionalService'],
    '@id': 'https://bramhaware.in/#organization',
    name: 'Bramhaware Technologies',
    alternateName: 'Bramhaware',
    url: 'https://bramhaware.in',
    logo: {
      '@type': 'ImageObject',
      url: 'https://bramhaware.in/bramhaware-logo-square.png',
      width: 512,
      height: 512,
    },
    image: 'https://bramhaware.in/og-bramhaware.png',
    telephone: '+91-78080-89090',
    email: 'info@bramhaware.in',
    priceRange: '₹₹',
    areaServed: 'IN',
    description:
      'Bramhaware Technologies is an IT services company based in Pune, India specializing in custom web applications, modern website development, and technical SEO architecture.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Pune',
      addressRegion: 'Maharashtra',
      addressCountry: 'IN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+91-78080-89090',
      contactType: 'customer service',
      email: 'info@bramhaware.in',
    },
    knowsAbout: [
      'Custom Web Application Development',
      'Modern Website Development',
      'Technical SEO & AEO Optimization',
      'Cross-Platform Mobile Apps',
      'Cloud DevOps & Architecture',
    ],
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      organization,
      {
        '@type': 'WebSite',
        '@id': 'https://bramhaware.in/#website',
        name: 'Bramhaware',
        alternateName: 'Bramhaware Technologies',
        url: 'https://bramhaware.in',
        publisher: { '@id': 'https://bramhaware.in/#organization' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQ_ITEMS.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  };

  return (
    <div className="mm-landing-page">
      <SEO
        title="Bramhaware Technologies | Custom Web Apps, Web Development & Technical SEO Studio"
        description="Bramhaware Technologies builds high-concurrency web applications, bespoke websites, and Page-1 technical SEO engines for ambitious ventures and enterprises. Based in Pune, India."
        keywords="Bramhaware Technologies, website development Pune, custom web apps, technical SEO company, full stack development, React Nextjs engineers, Cloud DevOps"
        canonicalUrl="/"
        structuredData={structuredData}
      />

      {/* ====================================================================
          STICKY HEADER & NAVIGATION
          ==================================================================== */}
      <header className="mm-header">
        <div className="mm-container mm-header-inner">
          <Link to="/" className="mm-brand" aria-label="Bramhaware Technologies Home">
            <img
              src="/brahmawarequotelogoonlyblue-removebg-preview.png"
              alt="Bramhaware Technologies Logo"
              className="mm-brand-logo-img"
              width="44"
              height="44"
            />
            <div className="mm-brand-text">
              <span className="mm-brand-title">
                BRAMHAWARE
                <span className="ink-stamp ink-stamp-crimson" style={{ fontSize: '0.6rem', padding: '2px 6px', transform: 'rotate(-2deg)' }}>
                  DEV
                </span>
              </span>
              <span className="mm-brand-tag">Engineering Studio // Pune</span>
            </div>
          </Link>

          <nav className="mm-nav" aria-label="Main Navigation">
            <ul className="mm-nav-links">
              <li>
                <a href="#about" className="mm-nav-link has-pin">
                  01. Manifesto
                </a>
              </li>
              <li>
                <a href="#services" className="mm-nav-link has-pin">
                  02. Services & Skills
                </a>
              </li>
              <li>
                <a href="#work" className="mm-nav-link has-pin">
                  03. Work Collage
                </a>
              </li>
              <li>
                <a href="#faq" className="mm-nav-link">
                  04. FAQ
                </a>
              </li>
            </ul>
          </nav>

          <div className="mm-header-actions">
            <a href="#rfp-section" className="mm-btn-primary">
              <span>Start Project</span>
              <ChevronRightIcon width="14" height="14" />
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* ==================================================================
            STAGE 1: HERO / COVER (BOLD & CLEAR IT AUTHORITY)
            ================================================================== */}
        <section className="mm-hero" id="hero">
          <div className="mm-container">
            <div className="mm-hero-grid">
              {/* Left Column: Bold Clear Copy + Conversion */}
              <div className="mm-hero-content">
                <div className="mm-hero-eyebrow-row">
                  <div className="mm-hero-badge">
                    <span className="mm-live-pulse"></span>
                    <span>PUNE ENGINEERING LABS</span>
                  </div>
                  <span className="ink-stamp ink-stamp-cyan">EST. 2024</span>
                  <span className="ink-stamp ink-stamp-amber">100% IN-HOUSE</span>
                </div>

                <h1 className="mm-hero-title">
                  We Architect Bespoke <span className="highlight-craft">Web Apps</span>, Modern Websites & Dominant{' '}
                  <span className="highlight-craft">Technical SEO</span>.
                </h1>

                <p className="mm-hero-desc">
                  Digital craft meets mission-critical software engineering. We don’t build generic template sites — we
                  engineer <strong>high-concurrency web applications</strong> with sub-second performance, bulletproof security,
                  and <strong>Page-1 organic search dominance</strong>.
                </p>

                {/* Instant Lead Capture Box */}
                <div className="mm-hero-actions">
                  <form onSubmit={handleHeroEmailSubmit} className="mm-hero-form">
                    <div className="washi-tape washi-tape-yellow" style={{ top: '-14px', left: '24px', width: '90px', transform: 'rotate(-3deg)' }}></div>
                    <input
                      type="email"
                      placeholder="Enter your work email for proposal..."
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      required
                      aria-label="Work Email"
                    />
                    <button type="submit" className="mm-btn-primary">
                      <span>Get Instant RFP</span>
                      <ChevronRightIcon width="14" height="14" />
                    </button>
                  </form>

                  {/* Hand-drawn scribble note pointing to form */}
                  <div className="mm-hero-arrow-note">
                    <svg viewBox="0 0 48 24">
                      <path d="M 6 18 Q 24 4 42 12" />
                      <polyline points="36 6 42 12 36 18" />
                    </svg>
                    <span>Instant architecture review in 4 hours</span>
                  </div>
                </div>

                {/* Trust Ticker / Metrics */}
                <div className="mm-trust-ticker">
                  <div className="mm-trust-metric">
                    <span className="mm-trust-metric-num">99.98%</span>
                    <span className="mm-trust-metric-label">Uptime SLA</span>
                  </div>
                  <div className="mm-trust-metric">
                    <span className="mm-trust-metric-num">&lt; 750ms</span>
                    <span className="mm-trust-metric-label">Avg Core Web Vitals</span>
                  </div>
                  <div className="mm-trust-metric">
                    <span className="mm-trust-metric-num">100%</span>
                    <span className="mm-trust-metric-label">Codebase Ownership</span>
                  </div>
                  <div className="mm-trust-metric">
                    <span className="mm-trust-metric-num">0%</span>
                    <span className="mm-trust-metric-label">Outsource Bloat</span>
                  </div>
                </div>
              </div>

              {/* Right Column: Tactile Mixed-Media Collage Showcase */}
              <DepthStage className="mm-hero-collage">
                {/* Washi tape pinning terminal top-left */}
                <div
                  className="washi-tape washi-tape-cyan"
                  style={{ top: '-14px', right: '40px', width: '130px', transform: 'rotate(4deg)', zIndex: 20 }}
                ></div>

                {/* Stamped badge */}
                <div className="mm-pinned-stamp-hero" style={{ '--depth': '56px' }}>
                  <span className="ink-stamp ink-stamp-crimson" style={{ padding: '8px 16px', fontSize: '0.8rem' }}>
                    ENGINEERED IN PUNE // VERIFIED
                  </span>
                </div>

                {/* Tactile Terminal Window */}
                <div className="mm-collage-terminal-card">
                  <div className="mm-terminal-header">
                    <div className="mm-terminal-dots">
                      <div className="mm-terminal-dot mm-dot-red"></div>
                      <div className="mm-terminal-dot mm-dot-yellow"></div>
                      <div className="mm-terminal-dot mm-dot-green"></div>
                    </div>
                    <span className="mm-terminal-title">brahmaware-architecture.ts</span>
                    <span style={{ fontSize: '0.65rem', color: '#64748b' }}>UTF-8</span>
                  </div>
                  <div className="mm-terminal-body">
                    <p className="mm-code-comment">{'// Bramhaware Production Standard'}</p>
                    <p>
                      <span className="mm-code-keyword">const</span> <span className="mm-code-func">bramhawareStudio</span> = {'{'}
                    </p>
                    <p style={{ paddingLeft: '16px' }}>
                      <span className="mm-code-prop">location</span>: <span className="mm-code-string">'Pune, MH, India'</span>,
                    </p>
                    <p style={{ paddingLeft: '16px' }}>
                      <span className="mm-code-prop">corePillars</span>: [
                    </p>
                    <p style={{ paddingLeft: '32px' }}>
                      <span className="mm-code-string">'Custom Web Applications'</span>,
                    </p>
                    <p style={{ paddingLeft: '32px' }}>
                      <span className="mm-code-string">'Modern Website Development'</span>,
                    </p>
                    <p style={{ paddingLeft: '32px' }}>
                      <span className="mm-code-string">'Dominant Technical SEO & AEO'</span>,
                    </p>
                    <p style={{ paddingLeft: '32px' }}>
                      <span className="mm-code-string">'Cloud DevOps & Microservices'</span>
                    </p>
                    <p style={{ paddingLeft: '16px' }}>],</p>
                    <p style={{ paddingLeft: '16px' }}>
                      <span className="mm-code-prop">performanceScore</span>: <span style={{ color: '#facc15' }}>100</span>,
                    </p>
                    <p style={{ paddingLeft: '16px' }}>
                      <span className="mm-code-prop">sla</span>: <span className="mm-code-string">'99.98% High-Availability'</span>,
                    </p>
                    <p>{'};'}</p>
                  </div>
                </div>

                {/* Yellow Sticky Note pinned over terminal */}
                <div className="sticky-note mm-pinned-note-hero" style={{ '--depth': '72px' }}>
                  <div className="washi-tape washi-tape-yellow" style={{ top: '-12px', left: '20px', width: '80px', transform: 'rotate(-4deg)' }}></div>
                  <div className="sticky-note-handtext">
                    "Zero junior-dev bait & switch. You work directly with veteran architects."
                  </div>
                  <span className="sticky-note-author">— Bramhaware Lead Eng.</span>
                </div>

                {/* Monospace Quick Specs Card */}
                <div className="mm-pinned-specs-card" style={{ '--depth': '40px' }}>
                  <div style={{ color: '#38bdf8', fontWeight: 'bold', marginBottom: '2px' }}>RUNTIME SPECS</div>
                  <div style={{ color: '#94a3b8' }}>Latency: 28ms Pune Edge</div>
                  <div style={{ color: '#34d399' }}>Status: Production Healthy</div>
                </div>
              </DepthStage>
            </div>
          </div>
        </section>

        {/* Paper Tear Divider */}
        <div className="paper-tear-divider"></div>

        {/* ==================================================================
            STAGE 2: ABOUT / TEAM (ENGINEERING MANIFESTO)
            ================================================================== */}
        <section className="mm-section" id="about">
          <div className="mm-container">
            <div className="mm-section-header">
              <div className="mm-section-tag">
                <span className="ink-stamp ink-stamp-cyan">STAGE 02 // PROFILE</span>
                <div className="washi-tape washi-tape-yellow" style={{ position: 'relative', height: '18px', width: '70px', transform: 'rotate(-2deg)' }}></div>
              </div>
              <h2 className="mm-section-title">
                The Anti-Agency Manifesto: <span className="marker-highlight">Code as Craft</span>
              </h2>
              <p className="mm-section-subtitle">
                Traditional agencies sell you slides, subcontract your codebase to juniors, and disappear when your
                database slows down. Bramhaware was built by engineers who treat software as precision craft.
              </p>
            </div>

            <div className="mm-about-grid">
              {/* Left Card: 3 Core Principles */}
              <div className="mm-scrapbook-card">
                <div className="washi-tape washi-tape-rose" style={{ top: '-12px', left: '32px', width: '100px', transform: 'rotate(-2deg)' }}></div>
                <div>
                  <h3 className="mm-manifesto-title">
                    <span>Our 3 Uncompromising Rules</span>
                  </h3>
                  <p className="mm-manifesto-body">
                    We believe modern web software should feel lightning-fast, look breathtaking, and generate measurable
                    business revenue on Day 1.
                  </p>

                  <ul className="mm-manifesto-principles">
                    <li className="mm-principle-item">
                      <span className="mm-principle-num">RULE 01</span>
                      <div className="mm-principle-text">
                        <strong>Sub-Second Performance by Default</strong>
                        <p>
                          Every millisecond of load latency kills conversion. We engineer server-side pre-rendering,
                          edge asset compression, and clean DOM trees that guarantee green Core Web Vitals.
                        </p>
                      </div>
                    </li>
                    <li className="mm-principle-item">
                      <span className="mm-principle-num">RULE 02</span>
                      <div className="mm-principle-text">
                        <strong>Zero Technical Debt Shortcuts</strong>
                        <p>
                          Strict TypeScript typing, modular domain-driven architecture, and automated test coverage.
                          Your team will never inherit an unmaintainable black box.
                        </p>
                      </div>
                    </li>
                    <li className="mm-principle-item">
                      <span className="mm-principle-num">RULE 03</span>
                      <div className="mm-principle-text">
                        <strong>Organic Search as Engineering Architecture</strong>
                        <p>
                          SEO is not just keyword stuffing. We bake Schema.org microdata, clean canonical hierarchies, and
                          Generative Search (AEO) signals into the application core.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span className="ink-stamp ink-stamp-mint">VERIFIED METHODOLOGY</span>
                  <span className="ink-stamp ink-stamp-amber">ISO STANDARDS</span>
                </div>
              </div>

              {/* Right Visual Collage: Studio Dossier + Team Craft Note */}
              <div className="mm-about-visual-collage">
                <div className="mm-studio-dossier-card">
                  <div className="washi-tape washi-tape-cyan" style={{ top: '-10px', right: '28px', width: '90px', transform: 'rotate(3deg)' }}></div>
                  <div className="mm-dossier-header">
                    <span className="mm-dossier-label">Bramhaware Engineering Dossier</span>
                    <span className="ink-stamp ink-stamp-crimson" style={{ fontSize: '0.65rem' }}>PUNE HQ</span>
                  </div>

                  <div className="mm-dossier-grid">
                    <div className="mm-dossier-stat">
                      <div className="mm-dossier-stat-val">100+</div>
                      <div className="mm-dossier-stat-desc">Production Deployments</div>
                    </div>
                    <div className="mm-dossier-stat">
                      <div className="mm-dossier-stat-val">4.9 / 5</div>
                      <div className="mm-dossier-stat-desc">Client Retention Rating</div>
                    </div>
                    <div className="mm-dossier-stat">
                      <div className="mm-dossier-stat-val">0</div>
                      <div className="mm-dossier-stat-desc">Outsourced Code</div>
                    </div>
                    <div className="mm-dossier-stat">
                      <div className="mm-dossier-stat-val">100%</div>
                      <div className="mm-dossier-stat-desc">On-Time Milestones</div>
                    </div>
                  </div>
                </div>

                {/* Overlapping Craft Note */}
                <div className="mm-team-craft-note">
                  <div className="washi-tape washi-tape-yellow" style={{ top: '-14px', left: '40px', width: '110px', transform: 'rotate(-3deg)' }}></div>
                  <div className="mm-team-quote">
                    "We don't just write code — we build digital assets that give Indian & global founders an unfair
                    competitive advantage."
                  </div>
                  <div className="mm-team-author">
                    Ajit Dandwate & The Bramhaware Engineering Squad // Pune, Maharashtra
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Paper Tear Divider */}
        <div className="paper-tear-divider"></div>

        {/* ==================================================================
            STAGE 3: SERVICES / SKILLS (CLEAN, SCANNABLE IT INDEX)
            ================================================================== */}
        <section className="mm-section" id="services">
          <div className="mm-container">
            <div className="mm-section-header text-center">
              <div className="mm-section-tag">
                <span className="ink-stamp ink-stamp-amber">STAGE 03 // SKILLS & SERVICES</span>
              </div>
              <h2 className="mm-section-title">
                Full-Cycle <span className="marker-highlight">Software Engineering</span> & Growth Architecture
              </h2>
              <p className="mm-section-subtitle">
                A clean, scannable index of our core IT capabilities. We combine handcrafted design aesthetics with
                enterprise-grade reliability.
              </p>
            </div>

            <div className="mm-services-grid">
              {/* Service 1: Custom Web Applications */}
              <div className="mm-service-card">
                <div className="washi-tape washi-tape-yellow mm-service-note" style={{ width: '100px', transform: 'rotate(-2deg)' }}></div>
                <div>
                  <div className="mm-service-card-top">
                    <div className="mm-service-icon-box">
                      <CodeIcon width="28" height="28" />
                    </div>
                    <span className="mm-service-index">PILLAR // 01</span>
                  </div>

                  <h3 className="mm-service-title">Custom Web Applications & SaaS</h3>
                  <p className="mm-service-desc">
                    Scalable, multi-tenant web applications engineered for heavy concurrency, real-time interactivity,
                    and bulletproof business logic.
                  </p>

                  <ul className="mm-service-features">
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Next.js 15 / React 19 server-side rendering</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Node.js & Spring Boot microservices</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>PostgreSQL & Redis distributed caching</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Role-based access control & enterprise auth</span>
                    </li>
                  </ul>
                </div>

                <div className="mm-tech-tags">
                  <span className="mm-tech-tag">React 19</span>
                  <span className="mm-tech-tag">Next.js</span>
                  <span className="mm-tech-tag">Spring Boot</span>
                  <span className="mm-tech-tag">PostgreSQL</span>
                  <span className="mm-tech-tag">TypeScript</span>
                </div>
              </div>

              {/* Service 2: Technical SEO & AEO */}
              <div className="mm-service-card">
                <div className="washi-tape washi-tape-cyan mm-service-note" style={{ width: '100px', transform: 'rotate(3deg)' }}></div>
                <div>
                  <div className="mm-service-card-top">
                    <div className="mm-service-icon-box" style={{ color: '#d97706', background: '#fffbeb', borderColor: 'rgba(217, 119, 6, 0.2)' }}>
                      <SearchIcon width="28" height="28" />
                    </div>
                    <span className="mm-service-index">PILLAR // 02</span>
                  </div>

                  <h3 className="mm-service-title">Technical SEO & AI Search Engine Optimization (AEO)</h3>
                  <p className="mm-service-desc">
                    Engineered search dominance. We optimize web architectures to claim top spots in both traditional
                    Google Search and generative AI engines.
                  </p>

                  <ul className="mm-service-features">
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Sub-second Core Web Vitals (LCP, CLS, INP)</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Deep Schema.org JSON-LD knowledge graphs</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Generative Engine Optimization (ChatGPT, Perplexity)</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Zero crawl-budget waste & edge dynamic rendering</span>
                    </li>
                  </ul>
                </div>

                <div className="mm-tech-tags">
                  <span className="mm-tech-tag">Core Web Vitals</span>
                  <span className="mm-tech-tag">Schema.org</span>
                  <span className="mm-tech-tag">SSR / SSG</span>
                  <span className="mm-tech-tag">AEO</span>
                  <span className="mm-tech-tag">Google SGE</span>
                </div>
              </div>

              {/* Service 3: Cloud Architecture & DevOps */}
              <div className="mm-service-card">
                <div className="washi-tape washi-tape-mint mm-service-note" style={{ width: '100px', transform: 'rotate(-1deg)' }}></div>
                <div>
                  <div className="mm-service-card-top">
                    <div className="mm-service-icon-box" style={{ color: '#059669', background: '#ecfdf5', borderColor: 'rgba(5, 150, 105, 0.2)' }}>
                      <CloudIcon width="28" height="28" />
                    </div>
                    <span className="mm-service-index">PILLAR // 03</span>
                  </div>

                  <h3 className="mm-service-title">Cloud DevOps & High-Concurrency Systems</h3>
                  <p className="mm-service-desc">
                    Zero-downtime automated deployment pipelines, resilient cloud architecture, and auto-scaling
                    infrastructures designed to absorb traffic spikes.
                  </p>

                  <ul className="mm-service-features">
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>AWS, Azure & Docker containerization</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Automated GitHub Actions CI/CD workflows</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Distributed load balancing & automated failover</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Bank-grade SSL/TLS, firewalls & data encryption</span>
                    </li>
                  </ul>
                </div>

                <div className="mm-tech-tags">
                  <span className="mm-tech-tag">Docker</span>
                  <span className="mm-tech-tag">Kubernetes</span>
                  <span className="mm-tech-tag">AWS EC2 / S3</span>
                  <span className="mm-tech-tag">CI / CD</span>
                  <span className="mm-tech-tag">Linux</span>
                </div>
              </div>

              {/* Service 4: Cross-Platform Mobile Apps */}
              <div className="mm-service-card">
                <div className="washi-tape washi-tape-rose mm-service-note" style={{ width: '100px', transform: 'rotate(2deg)' }}></div>
                <div>
                  <div className="mm-service-card-top">
                    <div className="mm-service-icon-box" style={{ color: '#e11d48', background: '#fff1f2', borderColor: 'rgba(225, 29, 72, 0.2)' }}>
                      <MobileIcon width="28" height="28" />
                    </div>
                    <span className="mm-service-index">PILLAR // 04</span>
                  </div>

                  <h3 className="mm-service-title">Cross-Platform Mobile App Engineering</h3>
                  <p className="mm-service-desc">
                    Native-speed mobile apps for iOS and Android powered by unified API backends, shared business
                    logic, and tactile interactive user interfaces.
                  </p>

                  <ul className="mm-service-features">
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Flutter & React Native cross-platform codebases</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Offline-first SQLite local caching</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>Push notifications, biometric login & sensors</span>
                    </li>
                    <li className="mm-service-feature-item">
                      <CheckIcon width="16" height="16" />
                      <span>End-to-end Apple App Store & Google Play publishing</span>
                    </li>
                  </ul>
                </div>

                <div className="mm-tech-tags">
                  <span className="mm-tech-tag">Flutter</span>
                  <span className="mm-tech-tag">React Native</span>
                  <span className="mm-tech-tag">iOS</span>
                  <span className="mm-tech-tag">Android</span>
                  <span className="mm-tech-tag">GraphQL</span>
                </div>
              </div>
            </div>

            {/* Direct Engineering Specifications & PDF Export Strip */}
            <div className="mm-dossier-banner">
              <div className="mm-dossier-banner-content">
                <span className="ink-stamp ink-stamp-cyan" style={{ fontSize: '0.62rem', padding: '3px 8px' }}>
                  SPECIFICATION ARCHIVE // 2026
                </span>
                <h4 style={{ margin: '8px 0 4px', fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 800, color: 'var(--mm-ink-title)' }}>
                  Complete Technical Dossier &amp; Architecture Brief
                </h4>
                <p style={{ margin: 0, fontSize: '0.88rem', color: 'var(--mm-ink-body)' }}>
                  Download our verified capabilities brief as an audit-ready PDF or inspect the interactive system blueprint.
                </p>
              </div>
              <div className="mm-dossier-banner-actions">
                <button
                  type="button"
                  onClick={downloadTechDossierPdf}
                  className="mm-btn-primary"
                  style={{ padding: '10px 20px', fontSize: '0.82rem' }}
                >
                  <DownloadIcon width="15" height="15" />
                  <span>Download Brief (PDF)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsDossierOpen(true)}
                  className="mm-btn-ghost"
                  style={{ padding: '10px 18px', fontSize: '0.82rem' }}
                >
                  <DocIcon width="15" height="15" />
                  <span>Interactive Blueprint</span>
                </button>
                <Link
                  to="/brag"
                  className="mm-btn-ghost"
                  style={{ padding: '10px 18px', fontSize: '0.82rem', textDecoration: 'none' }}
                >
                  <span>Milestones Archive (/brag) →</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Paper Tear Divider */}
        <div className="paper-tear-divider"></div>

        {/* ==================================================================
            STAGE 4: WORK / PROJECTS (OVERLAPPING MIXED-MEDIA SHOWCASE)
            ================================================================== */}
        <section className="mm-section" id="work">
          <div className="mm-container">
            <div className="mm-section-header">
              <div className="mm-section-tag">
                <span className="ink-stamp ink-stamp-crimson">STAGE 04 // WORK & FLEET</span>
                <div className="washi-tape washi-tape-cyan" style={{ position: 'relative', height: '18px', width: '80px', transform: 'rotate(2deg)' }}></div>
              </div>
              <h2 className="mm-section-title">
                Overlapping <span className="mm-nowrap"><span className="marker-highlight">Fleet Showcase</span>:</span> Real Production Case Studies
              </h2>
              <p className="mm-section-subtitle">
                Explore live web applications and platforms engineered in-house by Bramhaware. Every project showcases
                tactile craftsmanship paired with enterprise-grade stability.
              </p>
            </div>

            <div className="mm-projects-collage">
              {/* Project 1: Brilliant Share Market Academy (BSM) */}
              <div className="mm-project-row">
                <div className="mm-project-card">
                  <div className="washi-tape washi-tape-yellow" style={{ top: '-12px', left: '28px', width: '110px', transform: 'rotate(-3deg)' }}></div>
                  <div className="mm-project-card-header">
                    <span className="mm-project-category">EdTech & Financial LMS</span>
                    <span className="ink-stamp ink-stamp-amber">ACTIVE PRODUCTION</span>
                  </div>
                  <h3 className="mm-project-title">Brilliant Share Market Academy (BSM)</h3>
                  <p className="mm-project-desc">
                    A comprehensive financial academy platform featuring multi-campus admissions across Pune, Ahmednagar,
                    and Chh. Sambhajinagar. Includes automated fee ledgers, course registration, student certification, and
                    trading masterclass portals.
                  </p>

                  <div className="mm-project-impact-box">
                    <div className="mm-impact-text">
                      <strong>PRODUCTION IMPACT:</strong> 600+ active student enrollments, 99.9% LMS uptime, and Page 1
                      local SEO ranking for share market academy terms.
                    </div>
                  </div>

                  <div className="mm-project-footer">
                    <div className="mm-tech-tags">
                      <span className="mm-tech-tag">React</span>
                      <span className="mm-tech-tag">Node.js</span>
                      <span className="mm-tech-tag">Financial LMS</span>
                      <span className="mm-tech-tag">Local SEO</span>
                    </div>
                    <Link to="/brilliantsharemarket" className="mm-btn-primary" style={{ padding: '8px 16px', fontSize: '0.78rem' }}>
                      <span>View Live App</span>
                      <ExternalLinkIcon width="14" height="14" />
                    </Link>
                  </div>
                </div>

                {/* Polaroid Media Preview */}
                <div className="mm-project-media-polaroid">
                  <div className="washi-tape washi-tape-rose" style={{ top: '-10px', right: '24px', width: '90px', transform: 'rotate(4deg)' }}></div>
                  <div className="mm-polaroid-preview-window">
                    <div className="mm-polaroid-browser-bar">
                      <div className="mm-terminal-dots">
                        <div className="mm-terminal-dot mm-dot-red"></div>
                        <div className="mm-terminal-dot mm-dot-yellow"></div>
                        <div className="mm-terminal-dot mm-dot-green"></div>
                      </div>
                      <span className="mm-polaroid-browser-url">bramhaware.in/brilliantsharemarket</span>
                    </div>
                    <div className="mm-polaroid-inner-content">
                      <div className="mm-polaroid-inner-icon">
                        <CodeIcon width="28" height="28" />
                      </div>
                      <div className="mm-polaroid-inner-stat">600+ Students</div>
                      <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Multi-Campus Stock Trading LMS</span>
                    </div>
                  </div>
                  <div className="mm-polaroid-caption">"Share Market Academy Portal"</div>
                </div>
              </div>

              {/* Project 2: InvoiceGen Enterprise Billing */}
              <div className="mm-project-row reversed">
                {/* Polaroid Media Preview on Left */}
                <div className="mm-project-media-polaroid">
                  <div className="washi-tape washi-tape-cyan" style={{ top: '-10px', left: '24px', width: '90px', transform: 'rotate(-3deg)' }}></div>
                  <div className="mm-polaroid-preview-window">
                    <div className="mm-polaroid-browser-bar">
                      <div className="mm-terminal-dots">
                        <div className="mm-terminal-dot mm-dot-red"></div>
                        <div className="mm-terminal-dot mm-dot-yellow"></div>
                        <div className="mm-terminal-dot mm-dot-green"></div>
                      </div>
                      <span className="mm-polaroid-browser-url">bramhaware.in/invoicegen</span>
                    </div>
                    <div className="mm-polaroid-inner-content">
                      <div className="mm-polaroid-inner-icon" style={{ color: '#34d399' }}>
                        <DocIcon width="28" height="28" />
                      </div>
                      <div className="mm-polaroid-inner-stat">GST Compliant</div>
                      <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Instant PDF Billing Engine</span>
                    </div>
                  </div>
                  <div className="mm-polaroid-caption">"InvoiceGen Billing SaaS"</div>
                </div>

                <div className="mm-project-card">
                  <div className="washi-tape washi-tape-mint" style={{ top: '-12px', right: '28px', width: '110px', transform: 'rotate(2deg)' }}></div>
                  <div className="mm-project-card-header">
                    <span className="mm-project-category">Enterprise FinTech SaaS</span>
                    <span className="ink-stamp ink-stamp-mint">ACTIVE LEDGER</span>
                  </div>
                  <h3 className="mm-project-title">InvoiceGen: Enterprise GST Billing</h3>
                  <p className="mm-project-desc">
                    A high-speed browser-based invoice and estimate generation engine with real-time Indian GST tax
                    computation (CGST, SGST, IGST), customizable invoice themes, client address books, and instant vector
                    PDF downloads.
                  </p>

                  <div className="mm-project-impact-box">
                    <div className="mm-impact-text">
                      <strong>PRODUCTION IMPACT:</strong> 1,000+ invoices generated with zero math errors, sub-50ms PDF
                      rendering, and complete local privacy compliance.
                    </div>
                  </div>

                  <div className="mm-project-footer">
                    <div className="mm-tech-tags">
                      <span className="mm-tech-tag">React</span>
                      <span className="mm-tech-tag">jsPDF</span>
                      <span className="mm-tech-tag">GST Engine</span>
                      <span className="mm-tech-tag">State Sync</span>
                    </div>
                    <Link to="/invoicegen" className="mm-btn-primary" style={{ padding: '8px 16px', fontSize: '0.78rem' }}>
                      <span>Launch InvoiceGen</span>
                      <ExternalLinkIcon width="14" height="14" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Project 3: The Foundora Startup Network */}
              <div className="mm-project-row">
                <div className="mm-project-card">
                  <div className="washi-tape washi-tape-rose" style={{ top: '-12px', left: '28px', width: '110px', transform: 'rotate(-2deg)' }}></div>
                  <div className="mm-project-card-header">
                    <span className="mm-project-category">Venture & Startup Discovery</span>
                    <span className="ink-stamp ink-stamp-cyan">INNOVATION HUB</span>
                  </div>
                  <h3 className="mm-project-title">The Foundora: Startup & Angel Network</h3>
                  <p className="mm-project-desc">
                    An ecosystem portal connecting high-growth Indian founders with verified angel investors, venture
                    capital syndicates, and mentor advisory boards. Features startup directory indexing and founder profile
                    showcases.
                  </p>

                  <div className="mm-project-impact-box">
                    <div className="mm-impact-text">
                      <strong>PRODUCTION IMPACT:</strong> High-speed startup search, instant angel matchmaking workflows,
                      and complete schema knowledge graph metadata.
                    </div>
                  </div>

                  <div className="mm-project-footer">
                    <div className="mm-tech-tags">
                      <span className="mm-tech-tag">Next.js</span>
                      <span className="mm-tech-tag">GraphQL</span>
                      <span className="mm-tech-tag">Directory Indexing</span>
                      <span className="mm-tech-tag">Venture Tech</span>
                    </div>
                    <a
                      href="https://thefoundora.com"
                      target="_blank"
                      rel="noreferrer"
                      className="mm-btn-primary"
                      style={{ padding: '8px 16px', fontSize: '0.78rem' }}
                    >
                      <span>Explore The Foundora</span>
                      <ExternalLinkIcon width="13" height="13" />
                    </a>
                  </div>
                </div>

                <div className="mm-project-media-polaroid">
                  <div className="washi-tape washi-tape-yellow" style={{ top: '-10px', right: '24px', width: '90px', transform: 'rotate(2deg)' }}></div>
                  <div className="mm-polaroid-preview-window">
                    <div className="mm-polaroid-browser-bar">
                      <div className="mm-terminal-dots">
                        <div className="mm-terminal-dot mm-dot-red"></div>
                        <div className="mm-terminal-dot mm-dot-yellow"></div>
                        <div className="mm-terminal-dot mm-dot-green"></div>
                      </div>
                      <span className="mm-polaroid-browser-url">thefoundora.com</span>
                    </div>
                    <div className="mm-polaroid-inner-content">
                      <div className="mm-polaroid-inner-icon" style={{ color: '#38bdf8' }}>
                        <CloudIcon width="28" height="28" />
                      </div>
                      <div className="mm-polaroid-inner-stat">Discovery Network</div>
                      <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Founders & Angel Syndicates</span>
                    </div>
                  </div>
                  <div className="mm-polaroid-caption">"The Foundora Network"</div>
                </div>
              </div>

              {/* Project 4: NST Studio (client website) */}
              <div className="mm-project-row reversed">
                <div className="mm-project-media-polaroid">
                  <div className="washi-tape washi-tape-rose" style={{ top: '-10px', left: '24px', width: '90px', transform: 'rotate(-3deg)' }}></div>
                  <div className="mm-polaroid-preview-window">
                    <div className="mm-polaroid-browser-bar">
                      <div className="mm-terminal-dots">
                        <div className="mm-terminal-dot mm-dot-red"></div>
                        <div className="mm-terminal-dot mm-dot-yellow"></div>
                        <div className="mm-terminal-dot mm-dot-green"></div>
                      </div>
                      <span className="mm-polaroid-browser-url">nststudioin.netlify.app</span>
                    </div>
                    <div className="mm-polaroid-inner-content mm-polaroid-nst">
                      <img src="/assets/nst-logo-light.png" alt="NST Studio logo" width="180" height="69" />
                      <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Nikunj Storytelling Studio — Story First.</span>
                    </div>
                  </div>
                  <div className="mm-polaroid-caption">"NST Studio"</div>
                </div>

                <div className="mm-project-card">
                  <div className="washi-tape washi-tape-yellow" style={{ top: '-12px', right: '28px', width: '110px', transform: 'rotate(2deg)' }}></div>
                  <div className="mm-project-card-header">
                    <span className="mm-project-category">Creative Studio Website</span>
                    <span className="ink-stamp ink-stamp-crimson">CLIENT LAUNCH</span>
                  </div>
                  <h3 className="mm-project-title">NST Studio: Cinematic Storytelling Studio</h3>
                  <p className="mm-project-desc">
                    The web home of Nikunj Storytelling Studio (NST) — a storytelling-driven studio for branding, film,
                    animation and creative education. A "Story First." opening, a 3D WebGL spotlight stage with the NST
                    storyteller, a project archive and a full brand dossier.
                  </p>

                  <div className="mm-project-impact-box">
                    <div className="mm-impact-text">
                      <strong>DESIGN-TO-CODE:</strong> Built from the studio&rsquo;s own prototype and brand guide, with a
                      real-time 3D stage, mobile-first layouts and full reduced-motion support.
                    </div>
                  </div>

                  <div className="mm-project-footer">
                    <div className="mm-tech-tags">
                      <span className="mm-tech-tag">React 19</span>
                      <span className="mm-tech-tag">Three.js</span>
                      <span className="mm-tech-tag">TypeScript</span>
                      <span className="mm-tech-tag">Vite</span>
                    </div>
                    <a
                      href="https://nststudioin.netlify.app/"
                      target="_blank"
                      rel="noreferrer"
                      className="mm-btn-primary"
                      style={{ padding: '8px 16px', fontSize: '0.78rem' }}
                    >
                      <span>Visit NST Studio</span>
                      <ExternalLinkIcon width="14" height="14" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ==================================================================
            CONVERSION / RFP STUDIO DESK & FAQ
            ================================================================== */}
        <section className="mm-rfp-section" id="rfp-section">
          <div className="mm-container">
            <div className="mm-rfp-grid">
              {/* Left Column: Direct Inquiry Channels */}
              <div className="mm-rfp-details">
                <div className="mm-section-tag">
                  <span className="ink-stamp ink-stamp-crimson">CONVERSION DESK // RFP</span>
                  <div className="washi-tape washi-tape-yellow" style={{ position: 'relative', height: '18px', width: '80px', transform: 'rotate(-2deg)' }}></div>
                </div>

                <h2 className="mm-section-title">
                  Let’s Build Something <span className="marker-highlight">Extraordinary</span>.
                </h2>

                <p className="mm-section-subtitle">
                  Tell us about your web application, corporate website, or SEO objective. We review specifications and
                  return detailed architectural estimates within 4 business hours.
                </p>

                <div className="mm-contact-cards-stack">
                  <a href="tel:+917808089090" className="mm-contact-item">
                    <div className="mm-contact-item-icon">
                      <PhoneIcon width="20" height="20" />
                    </div>
                    <div className="mm-contact-item-info">
                      <strong>Studio Direct Helpline</strong>
                      <span>+91 78080 89090 (Calls & WhatsApp)</span>
                    </div>
                  </a>

                  <a href="mailto:info@bramhaware.in" className="mm-contact-item">
                    <div className="mm-contact-item-icon" style={{ color: '#facc15' }}>
                      <MailIcon width="20" height="20" />
                    </div>
                    <div className="mm-contact-item-info">
                      <strong>Engineering Dispatch Email</strong>
                      <span>info@bramhaware.in</span>
                    </div>
                  </a>

                  <div className="mm-contact-item">
                    <div className="mm-contact-item-icon" style={{ color: '#34d399' }}>
                      <PinIcon width="20" height="20" />
                    </div>
                    <div className="mm-contact-item-info">
                      <strong>Engineering Studio Location</strong>
                      <span>Pune, Maharashtra 411041, India</span>
                    </div>
                  </div>
                </div>

                {/* Tactile sticky note */}
                <div className="sticky-note" style={{ marginTop: '28px', maxWidth: '320px' }}>
                  <div className="washi-tape washi-tape-rose" style={{ top: '-10px', left: '20px', width: '80px', transform: 'rotate(2deg)' }}></div>
                  <div className="sticky-note-handtext">
                    "Every contract includes a strict 90-day post-launch code warranty."
                  </div>
                  <span className="sticky-note-author">— Bramhaware SLA Guarantee</span>
                </div>
              </div>

              {/* Right Column: Interactive RFP Form */}
              <div className="mm-rfp-form-card">
                <div className="washi-tape washi-tape-cyan" style={{ top: '-12px', right: '36px', width: '120px', transform: 'rotate(3deg)' }}></div>

                <div className="mm-rfp-form-header">
                  <h3 className="mm-rfp-form-title">Request a Project Proposal</h3>
                  <p className="mm-rfp-form-subtitle">Fill in your requirements below for an upfront architecture roadmap.</p>
                </div>

                {isSubmitted ? (
                  <div className="mm-form-success-banner">
                    <h4>Almost done — send your brief on WhatsApp</h4>
                    <p style={{ marginTop: '8px', fontSize: '0.92rem' }}>
                      Thank you, <strong>{quoteForm.name}</strong>. Your brief for <strong>{quoteForm.service}</strong> is
                      pre-filled in WhatsApp. Tap send and our engineering leads will reply within 4 business hours.
                    </p>
                    <a
                      href={buildWhatsAppBriefUrl(quoteForm)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mm-btn-primary"
                      style={{ marginTop: '16px' }}
                    >
                      WhatsApp didn't open? Send brief
                    </a>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="mm-btn-ghost"
                      style={{ marginTop: '16px' }}
                    >
                      Submit Another Inquiry
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleQuoteFormSubmit}>
                    <div className="mm-form-grid">
                      <div className="mm-form-group">
                        <label className="mm-form-label" htmlFor="rfp-name">Your Name *</label>
                        <input
                          id="rfp-name"
                          type="text"
                          name="name"
                          className="mm-form-input"
                          placeholder="e.g. Rahul Sharma"
                          value={quoteForm.name}
                          onChange={handleQuoteFormChange}
                          required
                        />
                      </div>

                      <div className="mm-form-group">
                        <label className="mm-form-label" htmlFor="rfp-email">Work Email *</label>
                        <input
                          id="rfp-email"
                          type="email"
                          name="email"
                          className="mm-form-input"
                          placeholder="rahul@company.com"
                          value={quoteForm.email}
                          onChange={handleQuoteFormChange}
                          required
                        />
                      </div>

                      <div className="mm-form-group">
                        <label className="mm-form-label" htmlFor="rfp-phone">Phone / WhatsApp</label>
                        <input
                          id="rfp-phone"
                          type="tel"
                          name="phone"
                          className="mm-form-input"
                          placeholder="+91 98765 43210"
                          value={quoteForm.phone}
                          onChange={handleQuoteFormChange}
                        />
                      </div>

                      <div className="mm-form-group">
                        <label className="mm-form-label" htmlFor="rfp-service">Service Required</label>
                        <select
                          id="rfp-service"
                          name="service"
                          className="mm-form-select"
                          value={quoteForm.service}
                          onChange={handleQuoteFormChange}
                        >
                          <option>Custom Web Application Development</option>
                          <option>High-Impact Website Development</option>
                          <option>Technical SEO & AEO Optimization</option>
                          <option>Cross-Platform Mobile App (Flutter/RN)</option>
                          <option>Cloud Architecture & DevOps</option>
                          <option>Comprehensive Retainer Squad</option>
                        </select>
                      </div>

                      <div className="mm-form-group">
                        <label className="mm-form-label" htmlFor="rfp-budget">Estimated Budget</label>
                        <select
                          id="rfp-budget"
                          name="budget"
                          className="mm-form-select"
                          value={quoteForm.budget}
                          onChange={handleQuoteFormChange}
                        >
                          <option>₹2L – ₹4L</option>
                          <option>₹4L – ₹12L</option>
                          <option>₹12L – ₹30L</option>
                          <option>₹30L+ Enterprise Tier</option>
                        </select>
                      </div>

                      <div className="mm-form-group">
                        <label className="mm-form-label" htmlFor="rfp-timeline">Target Launch</label>
                        <select
                          id="rfp-timeline"
                          name="timeline"
                          className="mm-form-select"
                          value={quoteForm.timeline}
                          onChange={handleQuoteFormChange}
                        >
                          <option>Immediate (Under 30 days)</option>
                          <option>1 to 2 Months</option>
                          <option>3 to 4 Months</option>
                          <option>Flexible / Exploratory</option>
                        </select>
                      </div>

                      <div className="mm-form-group full-width">
                        <label className="mm-form-label" htmlFor="rfp-message">Project Scope & Notes</label>
                        <textarea
                          id="rfp-message"
                          name="message"
                          className="mm-form-textarea"
                          placeholder="Briefly describe your objectives, key features, or link to existing wireframes/code..."
                          value={quoteForm.message}
                          onChange={handleQuoteFormChange}
                        ></textarea>
                      </div>
                    </div>

                    <button type="submit" className="mm-form-submit-btn">
                      <span>Send Project Brief on WhatsApp</span>
                      <ChevronRightIcon width="16" height="16" />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ==================================================================
            FAQ SECTION (SEARCH AUTHORITY & USER TRUST)
            ================================================================== */}
        <section className="mm-faq-section" id="faq">
          <div className="mm-container">
            <div className="mm-section-header text-center">
              <div className="mm-section-tag">
                <span className="ink-stamp ink-stamp-mint">ENGINEERING FAQ</span>
              </div>
              <h2 className="mm-section-title">
                Frequently Asked <span className="marker-highlight">Questions</span>
              </h2>
              <p className="mm-section-subtitle">
                Clear, transparent answers about our engineering process, technical SEO philosophy, and engagement models.
              </p>
            </div>

            <div className="mm-faq-list">
              {FAQ_ITEMS.map((item) => {
                const isActive = activeFaq === item.id;
                return (
                  <div key={item.id} className={`mm-faq-card ${isActive ? 'is-active' : ''}`}>
                    <button
                      className="mm-faq-header"
                      onClick={() => setActiveFaq(isActive ? '' : item.id)}
                      aria-expanded={isActive}
                    >
                      <span className="mm-faq-question">{item.question}</span>
                      <span className="mm-faq-toggle-icon">
                        <ChevronRightIcon width="16" height="16" />
                      </span>
                    </button>
                    {isActive && (
                      <div className="mm-faq-body">
                        <p>{item.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      {/* ====================================================================
          FOOTER
          ==================================================================== */}
      <footer className="mm-footer">
        <div className="mm-container">
          <div className="mm-footer-grid">
            {/* Col 1: Brand & Bio */}
            <div className="mm-footer-brand">
              <div className="mm-brand">
                <img
                  src="/brahmawarequotelogoonlyblue-removebg-preview.png"
                  alt="Bramhaware Technologies Logo"
                  className="mm-brand-logo-img"
                  width="44"
                  height="44"
                />
                <div className="mm-brand-text">
                  <span className="mm-brand-title">BRAMHAWARE</span>
                  <span className="mm-brand-tag">Engineering Studio // Pune</span>
                </div>
              </div>
              <p>
                Bramhaware Technologies architects high-concurrency web applications, responsive websites, and
                dominant technical SEO engines for ambitious businesses.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span className="ink-stamp ink-stamp-cyan" style={{ fontSize: '0.62rem', padding: '3px 8px' }}>
                  PUNE // MH // INDIA
                </span>
                <span className="ink-stamp ink-stamp-amber" style={{ fontSize: '0.62rem', padding: '3px 8px' }}>
                  99.98% SLA
                </span>
              </div>
            </div>

            {/* Col 2: Engineering Fleet */}
            <div>
              <h4 className="mm-footer-col-title">Production Fleet</h4>
              <ul className="mm-footer-links">
                <li>
                  <Link to="/brilliantsharemarket" className="mm-footer-link">
                    BSM Trading Academy
                  </Link>
                </li>
                <li>
                  <Link to="/invoicegen" className="mm-footer-link">
                    InvoiceGen Billing SaaS
                  </Link>
                </li>
                <li>
                  <Link to="/matrimony" className="mm-footer-link">
                    Matrimony Portal
                  </Link>
                </li>
                <li>
                  <Link to="/astrology" className="mm-footer-link">
                    Dnyanaai Astrology
                  </Link>
                </li>
                <li>
                  <a href="https://nststudioin.netlify.app/" target="_blank" rel="noreferrer" className="mm-footer-link">
                    NST Studio
                  </a>
                </li>
                <li>
                  <Link to="/brag" className="mm-footer-link">
                    Architecture Dossier
                  </Link>
                </li>
              </ul>
            </div>

            {/* Col 3: Services */}
            <div>
              <h4 className="mm-footer-col-title">Core Disciplines</h4>
              <ul className="mm-footer-links">
                <li>
                  <a href="#services" className="mm-footer-link">
                    Custom Web Applications
                  </a>
                </li>
                <li>
                  <a href="#services" className="mm-footer-link">
                    Technical SEO & AEO
                  </a>
                </li>
                <li>
                  <a href="#services" className="mm-footer-link">
                    Cloud DevOps & Docker
                  </a>
                </li>
                <li>
                  <a href="#services" className="mm-footer-link">
                    Cross-Platform Mobile Apps
                  </a>
                </li>
                <li>
                  <a href="#services" className="mm-footer-link">
                    Architecture Audits
                  </a>
                </li>
              </ul>
            </div>

            {/* Col 4: Contact & Office */}
            <div>
              <h4 className="mm-footer-col-title">Contact & Studio</h4>
              <ul className="mm-footer-links">
                <li>
                  <a href="tel:+917808089090" className="mm-footer-link">
                    +91 78080 89090
                  </a>
                </li>
                <li>
                  <a href="mailto:info@bramhaware.in" className="mm-footer-link">
                    info@bramhaware.in
                  </a>
                </li>
                <li style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: '1.4', marginTop: '6px' }}>
                  Pune, Maharashtra 411041, India
                </li>
              </ul>
            </div>
          </div>

          <div className="mm-footer-bottom">
            <div>&copy; {new Date().getFullYear()} Bramhaware Technologies. Handcrafted with precision in Pune, India.</div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <a href="#hero" className="mm-footer-link">
                Back to Top ↑
              </a>
              <button
                onClick={() => setIsDossierOpen(true)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                System Specs
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Complete Architecture & PDF Export Modal */}
      <TechDossierModal
        isOpen={isDossierOpen}
        onClose={() => setIsDossierOpen(false)}
        onNavigateToContact={() => {
          setIsDossierOpen(false);
          const rfpElement = document.getElementById('rfp-section');
          if (rfpElement) {
            rfpElement.scrollIntoView({ behavior: 'smooth' });
          }
        }}
      />
    </div>
  );
}
